import { RegisterComponent } from '@/common/register-component';
import { NylasSchedulerConfigConnector } from '@/connector/nylas-scheduler-config-connector';
import { debug } from '@/utils/utils';
import { AttachInternals, Component, Element, Event, EventEmitter, Host, Listen, Prop, State, Watch, h } from '@stencil/core';
import { NylasSchedulerEditor } from '../nylas-scheduler-editor/nylas-scheduler-editor';
import i18next from '@/utils/i18n';
import { LANGUAGE_CODE } from '@/common/constants';
import { Configuration, ThemeConfig } from '@nylas/core';

/**
 * The `nylas-only-specific-time-availability` component is a UI component that allows users to enable/disable specific time availability.
 * When enabled, users can configure specific time slots instead of using default open hours.
 * @part osta - The root container
 * @part osta__header - The header section with title and toggle
 * @part osta__header-content - The header content wrapper (title and description)
 * @part osta__header-title - The header title
 * @part osta__header-description - The header description
 * @part osta__toggle - The toggle switch wrapper
 * @part osta__info-box - The informational blue box
 * @part osta__info-box-title - The info box title
 * @part osta__info-box-description - The info box description
 */
@Component({
  tag: 'nylas-only-specific-time-availability',
  styleUrl: 'nylas-only-specific-time-availability.scss',
  shadow: true,
  formAssociated: true,
})
export class NylasOnlySpecificTimeAvailability {
  /**
   * The element <nylas-only-specific-time-availability> itself.
   */
  @Element() host!: HTMLNylasOnlySpecificTimeAvailabilityElement;

  /**
   * @standalone
   * The name of the component
   */
  @Prop() name: string = 'only-specific-time-availability';

  /**
   * @standalone
   * The selected configuration.
   */
  @Prop() selectedConfiguration?: Configuration;

  /**
   * The selected language.
   */
  @Prop() selectedLanguage?: LANGUAGE_CODE;

  /**
   * @standalone
   * The theme configuration.
   */
  @Prop({ attribute: 'theme-config' }) readonly themeConfig?: ThemeConfig;

  /**
   * @standalone
   * Whether only specific time availability is enabled.
   */
  @Prop() onlySpecificTimeAvailability?: boolean;

  /**
   * The enable specific time availability state.
   */
  @State() enableSpecificTimeAvailability: boolean = false;

  /**
   * The element internals.
   */
  @AttachInternals() internals!: ElementInternals;

  /**
   * This event is fired when the toggle switch is toggled.
   */
  @Event() valueChanged!: EventEmitter<{
    value: string;
    name: string;
  }>;

  /**
   * When a name prop is passed, stencil does not automatically set the name attribute on the host element.
   * Since this component is form-associated, the name attribute is required for form submission.
   * This is a workaround to ensure that the name attribute is set on the host element.
   */
  @Watch('name')
  elementNameChangedHandler(newValue: string) {
    debug('nylas-only-specific-time-availability', 'elementNameChangedHandler', newValue);
    this.host.setAttribute('name', newValue);
  }

  @Watch('selectedConfiguration')
  selectedConfigurationChangedHandler(newVal: Configuration) {
    debug('nylas-only-specific-time-availability', 'configChangedHandler', newVal);
    // Load the value from the prop
    this.loadOnlySpecificTimeAvailability();
  }

  @Watch('onlySpecificTimeAvailability')
  onlySpecificTimeAvailabilityChangedHandler(newVal: boolean | undefined, oldVal: boolean | undefined) {
    debug('nylas-only-specific-time-availability', 'onlySpecificTimeAvailabilityChangedHandler', newVal, oldVal);
    // Only update if the prop value is actually different from current state
    if (newVal !== undefined && newVal !== this.enableSpecificTimeAvailability) {
      this.loadOnlySpecificTimeAvailability();
    }
  }

  @Watch('selectedLanguage')
  selectedLanguageChangedHandler(newVal: LANGUAGE_CODE, oldVal: LANGUAGE_CODE) {
    if (newVal === oldVal) return;
    i18next.changeLanguage(newVal);
  }

  @Watch('themeConfig')
  themeConfigChangedHandler(newVal: ThemeConfig, oldVal: ThemeConfig) {
    if (newVal === oldVal) return;
    this.applyThemeConfig(newVal);
  }

  connectedCallback() {
    debug('nylas-only-specific-time-availability', 'connectedCallback');
    // Use queueMicrotask to defer theme application until after React has set props.
    // This fixes the timing issue where connectedCallback fires before React sets themeConfig.
    queueMicrotask(() => {
      if (this.themeConfig) {
        this.applyThemeConfig(this.themeConfig);
        this.host.setAttribute('data-themed', '');
      }
    });
  }

  disconnectedCallback() {
    debug('nylas-only-specific-time-availability', 'disconnectedCallback');
  }

  componentWillLoad() {
    debug('nylas-only-specific-time-availability', 'componentWillLoad');
    this.host.setAttribute('name', this.name);
  }

  componentDidLoad() {
    debug('nylas-only-specific-time-availability', 'componentDidLoad');
    if (this.selectedConfiguration) {
      this.selectedConfigurationChangedHandler(this.selectedConfiguration);
    } else if (this.onlySpecificTimeAvailability !== undefined) {
      this.loadOnlySpecificTimeAvailability();
    }
  }

  get isInternalsAvailable() {
    return typeof this.internals.setFormValue === 'function';
  }

  loadOnlySpecificTimeAvailability() {
    if (this.onlySpecificTimeAvailability !== undefined) {
      this.enableSpecificTimeAvailability = this.onlySpecificTimeAvailability;
      this.handleSettingValue(false);
    }
  }

  applyThemeConfig(themeConfig?: ThemeConfig) {
    if (themeConfig) {
      for (const [key, value] of Object.entries(themeConfig)) {
        this.host.style.setProperty(`${key}`, value);
      }
    }
  }

  @Listen('nylasFormSwitchToggled')
  nylasFormSwitchToggledHandler(event: CustomEvent<{ checked: boolean; name: string }>) {
    debug('nylas-only-specific-time-availability', 'nylasFormSwitchToggledHandler', event.detail);
    const { checked, name } = event.detail;
    if (name === this.name) {
      this.enableSpecificTimeAvailability = checked;
      this.handleSettingValue();
    }
  }

  handleSettingValue(valueChanged: boolean = true) {
    const value = JSON.stringify({
      enableSpecificTimeAvailability: this.enableSpecificTimeAvailability,
    });

    if (this.isInternalsAvailable) {
      this.internals.setFormValue(value, this.name);
    }

    if (valueChanged) {
      this.valueChanged.emit({
        value,
        name: this.name,
      });
    }
  }

  @RegisterComponent<NylasOnlySpecificTimeAvailability, NylasSchedulerConfigConnector, Exclude<NylasSchedulerEditor['stores'], undefined>>({
    name: 'nylas-only-specific-time-availability',
    stateToProps: new Map([
      ['schedulerConfig.selectedConfiguration', 'selectedConfiguration'],
      ['schedulerConfig.selectedLanguage', 'selectedLanguage'],
      ['schedulerConfig.themeConfig', 'themeConfig'],
    ]),
    fireRegisterEvent: true,
  })
  render() {
    return (
      <Host>
        <div class="nylas-only-specific-time-availability" part="osta">
          <div class="header" part="osta__header">
            <div class="header-content" part="osta__header-content">
              <h3 part="osta__header-title">
                {i18next.t('nylasOnlySpecificTimeAvailability.title')}
                <span class="title-icon">
                  <tooltip-component>
                    <info-icon slot="tooltip-icon" />
                    <span slot="tooltip-content">{i18next.t('nylasOnlySpecificTimeAvailability.tooltip.desc')}</span>
                  </tooltip-component>
                </span>
              </h3>
              <p part="osta__header-description">{i18next.t('nylasOnlySpecificTimeAvailability.description')}</p>
            </div>
            <div class="toggle-wrapper" part="osta__toggle">
              <toggle-switch name={this.name} checked={this.enableSpecificTimeAvailability} themeConfig={this.themeConfig} />
            </div>
          </div>

          {this.enableSpecificTimeAvailability && (
            <div class="info-box" part="osta__info-box">
              <div class="info-box-icon">
                <info-icon />
              </div>
              <div class="info-box-content">
                <h4 part="osta__info-box-title">{i18next.t('nylasOnlySpecificTimeAvailability.infoBox.title')}</h4>
                <p part="osta__info-box-description">{i18next.t('nylasOnlySpecificTimeAvailability.infoBox.description')}</p>
              </div>
            </div>
          )}
        </div>
      </Host>
    );
  }
}
